import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthenticatedHttpClient } from 'src/app/auth/authenticatedHttpClient';
import { catchError, throwError, firstValueFrom, lastValueFrom } from 'rxjs';
import { Training, UserTraining } from '../models/training.model';
import * as Sentry from '@sentry/angular';
import { UserRole } from 'src/app/auth/auth.service';
import { OnboardingRequestModalComponent } from 'src/app/shared/components/onboarding-request-modal/onboarding-request-modal.component';
import { ModalController } from '@ionic/angular';
import { ProfileService } from 'src/app/profile/services/profile.service';
import { TransferQualification } from 'src/app/transfers/model/transfer.model';

@Injectable({
  providedIn: 'root',
})
export class TrainingsService {
  private trainings: WritableSignal<Training[]> = signal([]);
  public getTrainings = this.trainings.asReadonly();

  public basicTrainings = computed(() =>
    this.trainings().filter((t) => t.level === 0),
  );

  public completedTrainings = computed(() =>
    this.trainings().filter((t) => t.isCompleted),
  );

  public extraTrainings = computed(() =>
    this.trainings().filter(
      (t) => t.type === 'qualifications' && t.level !== 1,
    ),
  );

  private apiUrl = `${environment.carmovaApiUrl}`;

  constructor(
    private readonly http: AuthenticatedHttpClient,
    private modalController: ModalController,
    private profileService: ProfileService,
  ) {}

  async fetchTrainings(currentUserRole: UserRole): Promise<void> {
    const suffix =
      currentUserRole !== 'Manager' ? 'driver/trainings' : 'manager/trainings';

    try {
      const trainings = await firstValueFrom(
        this.http.get<Training[]>(this.apiUrl + '/trainings/' + suffix).pipe(
          catchError((error) => {
            Sentry.captureException(error);

            return throwError(
              () => new Error('movacarpro_error_fetching_trainings'),
            );
          }),
        ),
      );

      this.trainings.set(trainings);
    } catch (error) {
      throw error;
    }
  }

  fetchTrainingCompletion(trainingId: string): Promise<UserTraining> {
    const completionType =
      this.profileService.profile.role === 'Manager'
        ? 'training-manager-completion'
        : 'training-driver-completion';

    return lastValueFrom(
      this.http.get<UserTraining>(
        this.apiUrl + '/trainings/' + completionType + '/' + trainingId,
      ),
    );
  }

  async getPremiumPartnerLevelByBasicTrainingsCompletion(
    trainings: Training[],
  ): Promise<number> {
    const allTrainingsCompleted = trainings.every((t) => t.isCompleted);

    return allTrainingsCompleted ? this.profileService.premiumPartnerLevel : 0;
  }

  async showOnboardingModal() {
    const modal = await this.modalController.create({
      component: OnboardingRequestModalComponent,
      backdropDismiss: false,
    });

    await modal.present();
  }

  getCompletedAndMissingQualifications(
    transferQualifications: TransferQualification[],
    driverQualifications: Training[],
  ) {
    const completedQualifications: TransferQualification[] = [];
    const missingQualifications: TransferQualification[] = [];

    transferQualifications?.forEach((qualification) => {
      const foundAndCompleted = driverQualifications?.find(
        (driverQualification) =>
          driverQualification.id === qualification.training.id &&
          driverQualification.isCompleted,
      );

      if (foundAndCompleted) {
        completedQualifications.push(qualification);
      } else {
        missingQualifications.push(qualification);
      }
    });

    return { completedQualifications, missingQualifications };
  }

  hasMissingQualifications(
    transferQualifications: TransferQualification[],
    completedTrainings: { id: string }[],
  ): boolean {
    return transferQualifications?.some(
      (qualification) =>
        !completedTrainings?.find(
          (driverQualification) =>
            driverQualification.id === qualification.training.id,
        ),
    );
  }

  mapUserTrainingsToTrainings(userTrainings: UserTraining[]) {
    return (
      userTrainings?.map((userTraining) => ({
        ...userTraining,
        ...userTraining.training,
        driverId: userTraining.driverId ?? '',
        driverRole: '',
      })) ?? []
    );
  }

  getCompletedTrainingIds(trainings: UserTraining[]) {
    return trainings
      ?.filter(
        (userTraining) => userTraining.isCompleted && userTraining.training.id,
      )
      ?.flatMap((userTraining) => ({
        id: userTraining.training.id!,
      }));
  }
}
